<template>
  <v-container
    v-if="$can('browse-sales', 'YachtModel')"
    fluid
  >
    <v-row
      align="center"
      no-gutters
      class="mb-4"
    >
      <v-col>
        <div class="headline">
          Yacht Models Pricelist - Sales Offers
        </div>
      </v-col>
    </v-row>

    <v-tabs
      v-model="filtersDirty.yacht_manufacturer_id"
      background-color="transparent"
    >
      <v-tab
        class="text-none caption px-2 py-2"
        style="min-width: 0px"
        href="#"
      >
        All
      </v-tab>
      <v-tab
        v-for="manufacturer in yachtManufacturers.data"
        :key="manufacturer.id"
        class="text-none caption px-2 py-2"
        style="min-width: 0px;"
        :href="'#' + manufacturer.id"
      >
        {{ manufacturer.name }}
      </v-tab>
    </v-tabs>
    <v-divider />

    <v-row>
      <v-col
        cols="3"
        class="pt-16"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <v-row
            dense
          >
            <v-col cols="16">
              <v-text-field
                v-model="filtersDirty.id"
                solo
                label="Yacht Model ID"
                hide-details
                clearable
                dense
                type="number"
                autocomplete="no"
              />
            </v-col>
            <v-col cols="16">
              <v-text-field
                v-model="filtersDirty.label"
                solo
                label="Model Name"
                hide-details
                clearable
                dense
                autocomplete="no"
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.country_id"
                solo
                label="Charter Country"
                :items="countries.data"
                :loading="countries.loading"
                item-text="name"
                item-value="id"
                dense
                autocomplete="disable"
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.yacht_type_id"
                solo
                label="Yacht Type"
                :items="yachtTypes.data"
                :loading="yachtTypes.loading"
                item-text="label"
                item-value="id"
                dense
                hide-details
                clearable
              />
            </v-col>
          </v-row>
          <v-subheader>
            Cabins
          </v-subheader>
          <v-row no-gutters>
            <v-col class="pr-1">
              <v-text-field
                v-model="filtersDirty.cabins_from"
                solo
                label="From"
                hide-details
                dense
                clearable
                autocomplete="no"
              />
            </v-col>
            <v-col class="pl-1">
              <currency-input
                v-model="filtersDirty.cabins_to"
                solo
                label="To"
                hide-details
                dense
                clearable
                autocomplete="no"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        cols="13"
      >
        <div>
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtModels.meta.from"
            :to="yachtModels.meta.to"
            :total="yachtModels.meta.total"
            :last-page="yachtModels.meta.last_page"
            :page.sync="pagination.page"
            class="mb-1 align-center"
          />
          <!--</editor-fold>-->
          <!--<editor-fold desc="Data">-->
          <v-data-iterator
            :items="yachtModels.data"
            :page.sync="pagination.page"
            :sort-by.sync="sorting.sortBy"
            :sort-desc.sync="sorting.desc"
            :headers="tableHeaders"
            :loading="yachtModels.loading"
            :server-items-length="yachtModels.meta.total"
            item-key="id"
            must-sort
            hide-default-footer
            class="mt-5 mb-5"
          >
            <template v-slot:default="{ items }">
              <v-row>
                <v-col
                  v-for="yachtModel in items"
                  :key="yachtModel.id"
                  cols="16"
                  sm="8"
                  md="8"
                  lg="4"
                >
                  <v-card
                    elevation="10"
                    class="fill-height"
                    @click="openView(yachtModel)"
                  >
                    <v-img
                      height="160px"
                      position="center"
                      :src="yachtModel.main_image ? yachtModel.main_image.path : undefined"
                    />
                    <v-card-text>
                      <div class="font-weight-bold">
                        {{ yachtModel.full_name }}
                      </div>
                      <div class="grey--text text--lighten-1 text-truncate">
                        <strong>{{ yachtModel.model_year }}</strong>
                        &middot;
                        ID {{ yachtModel.id }}
                        &middot;
                        <strong class="grey--text">
                          {{ yachtModel.cabin_count_description }}
                        </strong> cabins
                        &middot;
                        <strong class="grey--text">
                          {{ yachtModel.berth_count_description }}
                        </strong> RNOG
                      </div>
                      <div
                        v-if="yachtModel.engines_description"
                        class="grey--text text--lighten-1 text-truncate"
                      >
                        {{ yachtModel.engines_description }}
                      </div>
                      <v-divider class="my-2" />
                      <v-row
                        v-for="country in yachtModel.countries"
                        :key="country.id"
                        no-gutters
                        class="list--striped"
                      >
                        <v-col>
                          <flag
                            :iso="country.code"
                            :squared="false"
                            class="radius-3 mr-1"
                          />
                          {{ country.name }}
                        </v-col>
                        <v-col cols="auto">
                          {{ country.pivot.total_sale_price | currency(country.currency) }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </template>
            <template v-slot:no-data="{}">
              <!--<editor-fold desc="Filtered">-->
              <div
                v-if="isFiltered"
                class="text-center py-4"
              >
                <div>
                  There are no results for your search.
                </div>
                <div
                  class="mt-2"
                >
                  <v-btn
                    small
                    text
                    @click="clearFilters"
                  >
                    <v-icon left>
                      clear
                    </v-icon>
                    Clear Filters
                  </v-btn>
                </div>
              </div>
              <!--</editor-fold>-->
              <!--<editor-fold desc="NoData">-->
              <div
                v-else-if="!yachtModels.loading"
                class="text-center py-4"
              >
                <v-col cols="16">
                  There are no models at the moment.
                </v-col>
              </div>
              <!--</editor-fold>-->
              <div
                v-else
                class="text-center py-4"
              >
                Loading...
              </div>
            </template>
          </v-data-iterator>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtModels.meta.from"
            :to="yachtModels.meta.to"
            :total="yachtModels.meta.total"
            :last-page="yachtModels.meta.last_page"
            :page.sync="pagination.page"
            class="align-center"
          />
          <!--</editor-fold>-->
        </div>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-model-view-dialog
      v-if="viewYachtModelDialog.id"
      :yacht-model-id="viewYachtModelDialog.id"
      :country-id="viewYachtModelDialog.countryId"
      :visible.sync="viewYachtModelDialog.visible"
    />

    <yacht-edit-dialog
      :visible.sync="yachtEdit.visible"
      :yacht-model-id="yachtEdit.modelId"
      :country-id="yachtEdit.countryId"
      @update:yacht="yachtCreated"
    />
  </v-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'
import filterableMixin from '@/mixins/filterableMixin'
import paginationMixin from '@/mixins/paginationMixin'
import YachtModel from '@/models/YachtModel'
import YachtModelViewDialog from '@/components/business/yachtModels/YachtModelViewDialog'
import YachtEditDialog from '@/components/business/yachts/YachtEditDialog'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import { sortBy } from 'lodash'

export default {
  name: 'YachtModelsSalesOffers',
  components: {
    CurrencyInput,
    YachtEditDialog,
    YachtModelViewDialog
  },
  mixins: [paginationMixin, filterableMixin],
  data() {
    return {
      yachtModels: {
        data: [],
        loading: false,
        meta: {}
      },
      pagination: {
        perPage: 50
      },
      filtersClean: cloneDeep(YachtModel.emptyFilters),
      filtersDirty: cloneDeep(YachtModel.emptyFilters),
      viewYachtModelDialog: {
        visible: false,
        id: undefined,
        countryId: undefined
      },
      yachtEdit: {
        visible: false,
        modelId: undefined,
        countryId: undefined
      }
    }
  },
  computed: {
    tableHeaders() {
      return [
        { sortable: false, class: 'pr-0', width: '80px' },
        { text: 'Yacht Model', value: 'full_label', class: 'pl-5 pr-0' },
        { text: 'Model Year', value: 'model_year' },
        { text: 'For Country', value: 'country_id' },
        { text: 'Price', value: 'sale_price' },
        { sortable: false }
      ]
    },
    countries() {
      return sortBy(this.$store.getters['settings/charteringCountries'].data, 'name')
    },
    ...mapGetters('settings', ['yachtTypes', 'yachtManufacturers'])
  },
  watch: {
    includeDraftsFilter: function (value) {
      if (!value) {
        this.filtersDirty.status = YachtModel.STATUS_ACTIVE
        return
      }

      this.filtersDirty.status = null
    }
  },
  created() {
    this.yachtModelStatuses = YachtModel

    this.filtersDirty.status = YachtModel.STATUS_ACTIVE
    this.filtersDirty.is_sales_active = true

    this.sorting.sortBy = 'full_label'
    this.sorting.desc = false

    this.filtersDirty.model_year_from = null

    this.fetchYachtModels()
  },
  methods: {
    onPaginationUpdate() {
      this.fetchYachtModels()
    },
    onFiltersUpdate() {
      this.resetPagination()
    },
    async fetchYachtModels() {
      this.yachtModels.loading = true

      let queryParams = {
        ...this.paginationQueryParams,
        ...this.filterQueryParams

      }

      try {
        let result = await YachtModel.api.fetch(queryParams)

        this.yachtModels.data = result.data
        this.yachtModels.meta = result.meta
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
        throw e
      } finally {
        this.yachtModels.loading = false
      }
    },
    openView(yachtModel) {
      this.viewYachtModelDialog.id = yachtModel.id
      this.viewYachtModelDialog.visible = true
    },
    clearFilters() {
      this.filters = cloneDeep(YachtModel.emptyFilters)
    },
    openNewYacht(yachtModel) {
      this.yachtEdit.modelId = yachtModel.id
      this.yachtEdit.countryId = yachtModel.country.id
      this.yachtEdit.visible = true
    },
    yachtCreated(yacht) {
      this.$router.push({ name: 'yachtDashboard', params: { id: yacht.id } })
    }
  }
}
</script>
